import React, { useState, useCallback } from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { Modal } from 'design-system/Modal';
import { Checkbox, Button } from 'react-scale/components/design-system';
import { Textarea } from 'react-scale/components/design-system/Form';
import _ from 'lodash';
import { logEvent } from '@scale/frontend-lib/events';
import { Event } from '@scale/shared/types/events/index';
import { TaskerProductFeedbackArea } from '@scale/shared/types/events/TaskerProductFeedback';
import { useList } from 'react-use-v13';
import { WorkflowHook } from '../Sidebar/GuidedWorkflowPanel/useWorkflow';

const useStyles = makeStyles({
  feedbackContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
  },
  header: {
    fontSize: '20px',
    fontWeight: 'bold',
  },
  description: {
    margin: '16px 0 32 px',
    lineHeight: 1.7,
    fontSize: '16px',
    fontWeight: 'bold',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    margin: '18px 0px',
    justifyContent: 'space-between',
  },
});

interface GuidedWorkflowFeedbackModalProps {
  workflowHook: WorkflowHook;
}

const GuidedWorkflowFeedbackModal: React.FC<GuidedWorkflowFeedbackModalProps> = props => {
  const classes = useStyles();

  const [openFeedbackText, setOpenFeedbackText] = useState<string>('');
  const [
    bestFeatures,
    { push: addBestFeature, remove: removeBestFeature, reset: resetBestFeatures },
  ] = useList<string>([]);
  const [
    worstFeatures,
    { push: addWorstFeature, remove: removeWorstFeature, reset: resetWorstFeatures },
  ] = useList<string>([]);

  const { isFeedbackModalOpen, toggleIsFeedbackModalOpen } = props.workflowHook;

  const handleClickFeedbackButton: React.MouseEventHandler = e => {
    e.stopPropagation();
    toggleIsFeedbackModalOpen();
  };

  const handleToggleBestFeatureCheckbox = useCallback(
    (e: any, option: any) => {
      e.stopPropagation();
      if (bestFeatures.includes(option)) {
        removeBestFeature(_.findIndex(bestFeatures, feature => feature === option)!);
      } else {
        addBestFeature(option);
      }
    },
    [addBestFeature, bestFeatures, removeBestFeature],
  );

  const handleToggleWorstFeatureCheckbox = useCallback(
    (e: any, option: any) => {
      e.stopPropagation();
      if (worstFeatures.includes(option)) {
        removeWorstFeature(_.findIndex(worstFeatures, feature => feature === option)!);
      } else {
        addWorstFeature(option);
      }
    },
    [addWorstFeature, removeWorstFeature, worstFeatures],
  );

  const handleSubmit = useCallback(
    (e: any) => {
      e.stopPropagation();
      logEvent(Event.TaskerProductFeedback, {
        feedbackArea: TaskerProductFeedbackArea.GuidedWorkflowBar,
        feedbackData: {
          version: 1.0,
          bestFeatures,
          worstFeatures,
          openFeedback: openFeedbackText,
        },
      });
      toggleIsFeedbackModalOpen();
      setOpenFeedbackText('');
      resetBestFeatures();
      resetWorstFeatures();
    },
    [
      bestFeatures,
      openFeedbackText,
      resetBestFeatures,
      resetWorstFeatures,
      toggleIsFeedbackModalOpen,
      worstFeatures,
    ],
  );

  const renderBestFeaturesFeedback = (question: string, options: string[]) => {
    return (
      <div>
        <h4>{question}</h4>
        {_.map(options, option => {
          return (
            <div>
              <Checkbox
                key={option}
                label={option}
                checked={bestFeatures.includes(option)}
                disabled={bestFeatures.length >= 3 && !bestFeatures.includes(option)}
                onChange={e => handleToggleBestFeatureCheckbox(e, option)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderWorstFeaturesFeedback = (question: string, options: string[]) => {
    return (
      <div>
        <h4>{question}</h4>
        {_.map(options, option => {
          return (
            <div>
              <Checkbox
                key={option}
                label={option}
                checked={worstFeatures.includes(option)}
                disabled={worstFeatures.length >= 3 && !worstFeatures.includes(option)}
                onChange={e => handleToggleWorstFeatureCheckbox(e, option)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderOpenFeedback = () => {
    return (
      <>
        <h4> Please leave any other feedback you have here </h4>
        <Textarea
          onChange={e => setOpenFeedbackText(e.target.value)}
          value={openFeedbackText}
          placeholder="Guided Workflows could be better in...."
        ></Textarea>
      </>
    );
  };

  const renderButtons = () => {
    return (
      <div className={classes.buttonContainer}>
        <Button type="error" onClick={toggleIsFeedbackModalOpen}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit}>
          Submit Feedback
        </Button>
      </div>
    );
  };

  // TODO: Replace with better questions
  const bestFeaturesQuestion = {
    question: 'What are the most useful features of the Guided Workflow?',
    options: [
      'Step by Step guide for tasker',
      'Instructions being targeted to a specific step',
      'New instruction panel on the right',
      'Effects when you change steps',
      'Annotations List (right side bar) Check Boxes',
    ],
  };

  const worstFeaturesQuestion = {
    question: 'What are the things we should improve the most / you would like changed? ',
    options: [
      'General Navigation',
      'ToolTip Display (pop up with the details)',
      'Annotations List (right side bar) Check Boxes',
      'New instruction panel on the right',
    ],
  };

  return (
    <div>
      <Button type="primary" onClick={handleClickFeedbackButton}>
        Leave feedback
      </Button>
      {isFeedbackModalOpen && (
        <Modal isOpen={isFeedbackModalOpen} onRequestClose={toggleIsFeedbackModalOpen}>
          <div className={classes.feedbackContainer}>
            <div className={classes.header}>Feedback for Guided Workflow Bar</div>
            <div className={classes.description}>
              {' '}
              We are dedicated to trying to improve Guided Workflow to be useful tool for all
              taskers. We would love your feedback to improve.
            </div>
            <div className={classes.description}>
              {' '}
              Note - This feedback is for the Guided Workflow tool, not feedback for the tasker you
              are reviewing.
            </div>
            {renderBestFeaturesFeedback(
              bestFeaturesQuestion.question,
              bestFeaturesQuestion.options,
            )}
            {renderWorstFeaturesFeedback(
              worstFeaturesQuestion.question,
              worstFeaturesQuestion.options,
            )}
            {renderOpenFeedback()}
            {renderButtons()}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GuidedWorkflowFeedbackModal;
